import classnames from 'classnames'
import React from 'react'
import { Color, mq } from 'marketing-site/src/library/utils'
import { HeaderNavigationMenuSection } from './headerNavigationMenuSection'
import { HeaderNavigationSpotlight } from './headerNavigationSpotlight'
import {
  IHeaderNavigationMenu,
  IHeaderNavigationMenuSection,
  IHeaderNavigationSpotlight,
  isHeaderNavigationSection,
  isSectionWide,
} from './index'
import { BrandOptions, useBrand } from '../../providers/brandProvider'
import { HeaderNavigationMenu as HeaderNavigationMenuLegacy } from './headerNavigationMenuLegacy'

export const HeaderNavigationMenu = (props: IProps): React.ReactElement => {
  const theme = useBrand()
  if (theme === BrandOptions.default) {
    return <HeaderNavigationMenuDefault {...props} />
  }
  return <HeaderNavigationMenuLegacy {...props} />
}

const GRID_SPOTLIGHT_WIDTH_SMALL = '2fr'
const GRID_SPOTLIGHT_WIDTH_DEFAULT = '3fr'
const GRID_SPOTLIGHT_WIDTH_BIG = '4fr'

export interface IProps {
  navigationElement: IHeaderNavigationMenu
  active: boolean
}

function getSectionsGridTemplate(
  sections: (IHeaderNavigationMenuSection | IHeaderNavigationSpotlight)[],
) {
  const hasFullSections = sections.length === 4
  const spacer = hasFullSections ? '1fr' : '0.5fr'
  return sections
    .map((section, index) => {
      if (isHeaderNavigationSection(section)) {
        let length = ''

        if (hasFullSections) {
          length = isSectionWide(section) ? '4fr' : '2fr'
        } else {
          length = isSectionWide(section) ? '3fr' : '1fr'
        }

        return `${index > 0 ? ` ${spacer} ` : ''}${length}${
          index === sections.length - 1 ? ` ${spacer}` : ''
        }`
      } else {
        let spotlightSize = GRID_SPOTLIGHT_WIDTH_DEFAULT
        if (hasFullSections) {
          if (index === sections.length - 1) {
            spotlightSize = GRID_SPOTLIGHT_WIDTH_BIG
          }
        } else {
          spotlightSize = GRID_SPOTLIGHT_WIDTH_SMALL
          if (index === sections.length - 1) {
            spotlightSize = GRID_SPOTLIGHT_WIDTH_DEFAULT
          }
        }
        return `${index > 0 ? ` ${spacer} ` : ''}${spotlightSize}`
      }
    })
    .join('')
}

export function HeaderNavigationMenuDefault({
  navigationElement,
  active,
}: IProps): React.ReactElement {
  const { title, sections } = navigationElement
  const isLastSectionNotSpotlight =
    sections && isHeaderNavigationSection(sections[sections.length - 1])
  return (
    <div className={classnames('menu', { active })} data-testid="header-navigation-menu">
      <div
        className={classnames('menu-wrapper', {
          'menu-wrapper--right-padding': isLastSectionNotSpotlight,
        })}
        key={title}
        aria-haspopup="true"
        aria-expanded={active}
      >
        <div className="spacer"></div>
        <div className="sections">
          {sections &&
            sections.map((section, index) => {
              if (isHeaderNavigationSection(section)) {
                return (
                  <>
                    {index > 0 && <div className="section-divider"></div>}
                    <HeaderNavigationMenuSection key={section.title} {...section} />
                    {index === sections.length - 1 && <div className="section-divider"></div>}
                  </>
                )
              } else {
                return (
                  <>
                    {/* This empty div is needed for the grid layout, to create
                    spacing before the spotlight */}
                    {index > 0 && <div></div>}
                    <div
                      className={classnames('menu-spotlight', {
                        'menu-spotlight--first-section': index === 0,
                        'menu-spotlight--last-section': index === sections.length - 1,
                      })}
                    >
                      <HeaderNavigationSpotlight key={section.title} {...section} />
                    </div>
                  </>
                )
              }
            })}
        </div>
      </div>

      <style jsx>{`
        .menu {
          position: fixed;
          margin: 0 auto;
          top: var(--header-height);
          left: 0;
          right: 0;

          /* Add 3D rotation on open/close */
          &.active {
            opacity: 1;
            pointer-events: auto;
          }
          opacity: 0;
          pointer-events: none;
          --menu-sections-py: 25px;
        }

        .menu-wrapper {
          box-shadow: 0px 10px 18px rgba(0, 0, 0, 0.05);
          background-color: ${Color.White};
          padding-left: calc(
            max((100% - #{$container-max-width}) / 2, var(--header-navigation-compact-px))
          );
        }

        .spacer {
          position: fixed;
          left: 0;
          right: 0;
          background-color: ${Color.Stone};
          height: 1px;
        }

        .sections {
          display: grid;
          grid-template-columns: ${getSectionsGridTemplate(sections)};
          justify-content: space-between;
        }

        .section-divider {
          width: 30px;
        }

        @media (${mq.desktop}) {
          .menu {
            &.active {
              transform: rotateX(0);
            }
            // shift origin up for larger arc of rotation
            transform-origin: center -20px;
            transform: rotateX(-15deg);
            transition: transform $timing-fast, opacity $timing-fast;

            .sections {
              .menu-spotlight {
                :global(.spotlight) {
                  height: 100%;
                }

                &--first-section {
                  :global(.spotlight) {
                    padding-right: 0;
                  }
                }
              }
            }
          }
        }

        @media (${mq.desktopLg}) {
          .sections {
            display: flex;

            .menu-spotlight {
              &:not(.menu-spotlight--last-section) {
                :global(.spotlight) {
                  padding-right: 3rem;
                }
              }

              &--first-section {
                :global(.spotlight) {
                  margin-left: 0;
                }
              }

              &--last-section {
                :global(.spotlight) {
                  --padding-right: calc((100vw - #{$container-max-width}) / 2);
                  padding-right: var(--padding-right);
                }
              }
            }
          }

          .menu-wrapper {
            &--right-padding {
              --padding-right: calc((100vw - 1290px) / 2);
              padding-right: var(--padding-right);
            }
          }
        }

        @media (${mq.desktopXLg}) {
          .sections {
            .menu-spotlight {
              &:not(.menu-spotlight--last-section) {
                :global(.spotlight) {
                  padding-right: 0;
                  max-width: 25rem;
                }
              }
              &--first-section {
                :global(.spotlight) {
                  padding-left: 0;
                }
              }
            }
          }
        }
      `}</style>
    </div>
  )
}
